import { RightArrowicon } from '@components/Icons/RightArrowIcon';
import { WisdomIcon } from '@components/Icons/WisdomIcon';
import { useStationMode } from '@hooks/station';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Album } from '../../../../types';
import { HomeWisdomGrid } from './HomeWisdomGrid';

interface HomeWisdomProps {
  albums: Album[];
  loading: boolean;
  error: string | null;
  filterByGenre: (
    category: 'music' | 'serenity' | 'books',
    genre: string,
  ) => Album[];
  genres?: string[]; // Genres extracted from featured albums data (no API call)
}

export const HomeWisdom = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  albums: _albums,
  loading: isLoading,
  error: displayError,
  filterByGenre,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  genres: _genres,
}: HomeWisdomProps) => {
  const { t } = useTranslation();
  const isStationMode = useStationMode();
  // Use all albums without genre filtering
  const filteredItems = useMemo(() => {
    return filterByGenre('books', 'All');
  }, [filterByGenre]);

  return (
    <div id="audiobooks-page" className="font-[NapsterSans] mb-10 w-full">
      <div className="flex items-center justify-between mb-5 font-[NapsterSans] px-4 sm:px-6">
        <div className="flex gap-[6px] sm:gap-[8px] items-center hover-transition">
          <div className="flex">
            <WisdomIcon />
          </div>
          <Link
            to="/audiobooks"
            className="flex gap-[6px] sm:gap-[8px] items-center text-[15px] sm:text-[18px] text-white hover:text-[#fcfcfc] transition-all duration-500 ease-in-out font-bold leading-[normal] cursor-pointer group">
            {t('home.audiobooks')}
            <span className="hidden sm:inline">
              <RightArrowicon />
            </span>
          </Link>
        </div>
        <Link
          to="/audiobooks"
          className="text-xs sm:text-sm text-white hover:text-[#fcfcfc] hover:underline transition-all duration-300 ease-in-out cursor-pointer">
          {t('home.showAll')}
        </Link>
      </div>

      <div className="flex text-xs font-medium leading-[130%] text-white/80 text-left w-full px-4 mt-2 sm:hidden sm:px-6">
        {t('home.audiobooksDescription')}
      </div>

      {/* Initial Loading State */}
      {isLoading && filteredItems.length === 0 && (
        <div className="relative w-full group overflow-hidden px-4 sm:px-6">
          <div className="flex gap-[16px] overflow-x-auto snap-x snap-mandatory scroll-smooth hide-scrollbar scrollbar-hide select-none scroll-pl-4 sm:scroll-pl-6">
            {[...Array(4)].map((_, index) => (
              <div
                key={`loading-${index}`}
                className={`flex-shrink-0 snap-start ${
                  isStationMode ? 'w-[282px]' : 'w-[371px]'
                }`}>
                <div
                  className={`relative w-full rounded-[16px] animate-pulse overflow-hidden shadow-[0_4px_16px_0_rgba(0,0,0,0.40)] ${
                    isStationMode ? 'h-[395px]' : 'h-[520px]'
                  }`}
                  style={{
                    background:
                      'linear-gradient(180deg, #3B4758 0%, #000000 100%)',
                  }}>
                  {/* Badge skeleton */}
                  <div className="absolute top-[16px] left-[16px] z-[100] w-[110px] h-[40px] bg-gray-600/50 rounded-full"></div>

                  {/* Center Book Cover skeleton */}
                  <div className="absolute inset-0 flex top-[80px] justify-center z-[50]">
                    <div className="w-[185px] h-[275.1px] rounded-[8px] bg-gray-600/50 shadow-lg"></div>
                  </div>

                  {/* Bottom Section skeleton */}
                  <div className="absolute z-[100] bottom-0 left-0 p-5 flex flex-col gap-3 w-full">
                    {/* Album name skeleton */}
                    <div className="h-[28px] bg-gray-600/50 rounded w-4/5"></div>

                    {/* Contributor info skeleton */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 flex-1">
                        <div className="w-[56px] h-[56px] rounded-full bg-gray-600/50 flex-shrink-0"></div>
                        <div className="h-[18px] bg-gray-600/50 rounded w-32"></div>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="h-[12px] bg-gray-600/50 rounded w-8"></div>
                        <div className="w-[14px] h-[14px] bg-gray-600/50 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error State */}
      {displayError && !isLoading && (
        <div className="flex justify-center items-center py-12 px-4 sm:px-6">
          <div className="text-center">
            <div className="text-red-400 mb-4">
              <svg
                className="w-8 h-8 mx-auto mb-2"
                fill="currentColor"
                viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm font-medium">
                {t('home.failedToLoadAudiobooks')}
              </p>
              <p className="text-xs text-red-300 mt-1">{displayError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Empty State - No audiobooks content available */}
      {!displayError && !isLoading && filteredItems.length === 0 && (
        <div
          className={`flex items-center justify-center py-12 px-4 sm:px-6 ${
            isStationMode ? 'h-[395px]' : 'h-[520px]'
          }`}>
          <div className="text-center text-white/60">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-white/40"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            <p className="text-lg font-medium mb-2">
              {t('home.noAudiobooksAvailable')}
            </p>
            <p className="text-sm text-white/40">
              {t('home.noTrendingAudiobooks')}
            </p>
          </div>
        </div>
      )}

      {/* Content */}
      {!displayError && (
        <>
          <HomeWisdomGrid albums={filteredItems} />
        </>
      )}
    </div>
  );
};
